import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthUser, RoleSlug } from '@lms/shared-types';
import { userHierarchyLevel } from '@lms/shared-types';
import { extractUserRoleSlugs } from '@/lib/user-roles';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isHydrated: boolean;
  setUser: (user: AuthUser) => void;
  markAuthenticated: () => void;
  clearSession: () => void;
  setHydrated: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isHydrated: false,
      setUser: (user) =>
        set({
          user: {
            ...user,
            roles: extractUserRoleSlugs(user.roles) as RoleSlug[],
          },
        }),
      markAuthenticated: () => set({ isAuthenticated: true }),
      clearSession: () => set({ user: null, isAuthenticated: false }),
      setHydrated: () => set({ isHydrated: true }),
    }),
    {
      name: 'lms-auth',
      version: 3,
      partialize: (state) => ({
        user: state.user
          ? {
              id: state.user.id,
              email: state.user.email,
              name: state.user.name,
              roles: state.user.roles,
            }
          : null,
      }),
      migrate: () => ({
        user: null,
        isAuthenticated: false,
        isHydrated: false,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isAuthenticated = false;
          if (state.user) {
            state.user = {
              id: state.user.id,
              email: state.user.email,
              name: state.user.name,
              roles: extractUserRoleSlugs(state.user.roles) as RoleSlug[],
              permissions: [],
              hierarchyLevel: state.user.hierarchyLevel ?? userHierarchyLevel(extractUserRoleSlugs(state.user.roles)),
            };
          }
          state.setHydrated();
        }
      },
    },
  ),
);

export const selectIsAuthenticated = (state: AuthState) =>
  Boolean(state.isAuthenticated && state.user);
